/**
 * Formation glyph: the position-colored alignment picture on a formation
 * button, drawn from the catalog row's `glyph_svg` (`enum_values.glyph_svg`).
 *
 * The artwork is server-owned so a tenant can attach a picture to its own
 * formations later without a web deploy. The markup is the INNER content of a
 * `viewBox="0 0 120 72"` SVG; the component supplies the sized wrapper.
 * Inline `<svg>` rather than `<img>` so it inherits `currentColor` for the
 * line of scrimmage and scales with the button.
 *
 * Defence in depth: the platform only ever stores generated static shapes,
 * but the string still crosses a trust boundary, so anything that could run
 * script or load a remote resource is stripped before it reaches the DOM.
 */
import { useMemo } from 'react';

export const FORMATION_GLYPH_VIEW_BOX = '0 0 120 72';

const ALLOWED_ELEMENTS = new Set([
  'g',
  'circle',
  'ellipse',
  'rect',
  'line',
  'polyline',
  'polygon',
  'path',
]);

/**
 * Keep only static shape elements and presentation attributes. Returns null
 * when nothing drawable survives so the caller can fall back to text.
 */
export function sanitizeGlyphSvg(markup: string | null | undefined): string | null {
  if (!markup || typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`,
    'image/svg+xml'
  );
  const root = doc.documentElement;
  if (root.querySelector('parsererror')) return null;

  const walk = (node: Element): void => {
    for (const child of [...node.children]) {
      if (!ALLOWED_ELEMENTS.has(child.localName)) {
        child.remove();
        continue;
      }
      for (const attribute of [...child.attributes]) {
        const name = attribute.name.toLowerCase();
        if (name.startsWith('on') || name.includes('href') || name === 'style') {
          child.removeAttribute(attribute.name);
        }
      }
      walk(child);
    }
  };
  walk(root);
  const inner = root.innerHTML.trim();
  return inner === '' ? null : inner;
}

const POS_LEGEND = [
  { role: 'QB', color: '#FBBF24' },
  { role: 'RB', color: '#FB923C' },
  { role: 'WR', color: '#38BDF8' },
  { role: 'TE', color: '#A78BFA' },
] as const;

/** Tiny QB·RB·WR·TE swatch legend for the formation cards. */
export function FormationGlyphLegend(): JSX.Element {
  return (
    <div className="formation-glyph-legend" aria-hidden="true">
      {POS_LEGEND.map((item) => (
        <span key={item.role} className="formation-glyph-legend__item">
          <span className="formation-glyph-legend__swatch" style={{ background: item.color }} />
          {item.role}
        </span>
      ))}
    </div>
  );
}

/** The catalog value's glyph, or null when the row carries no artwork. */
export function FormationGlyph({
  glyphSvg,
  label,
}: {
  glyphSvg: string | null | undefined;
  label: string;
}): JSX.Element | null {
  const safe = useMemo(() => sanitizeGlyphSvg(glyphSvg), [glyphSvg]);
  if (safe === null) return null;
  return (
    <svg
      className="formation-glyph"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={FORMATION_GLYPH_VIEW_BOX}
      fill="none"
      aria-hidden="true"
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
    >
      <title>{label}</title>
      <g dangerouslySetInnerHTML={{ __html: safe }} />
    </svg>
  );
}
